/**
 * Consultazione dei log (spec §8.5).
 *
 * ## Perché serve una pagina e non basta il database
 *
 * Su SharePoint ogni operazione risulta fatta dall'identità applicativa del
 * portale: chi ha scaricato un file, e per conto di quale organizzazione, esiste
 * **solo** in `audit_log`. È il registro da aprire quando un cliente contesta un
 * documento o quando qualcuno chiede conto di un accesso.
 *
 * ## Due scelte che vale la pena giustificare
 *
 * **Nessun filtro attivo all'apertura.** Sarebbe comodo nascondere le
 * `list` — la navigazione è di gran lunga la riga più frequente e riempie lo
 * schermo. Ma su un registro di prove un filtro predefinito è una trappola: chi
 * guarda conclude «non è mai avvenuto» quando la verità è «non è mai stato
 * cercato». Si parte da tutto e si restringe a mano.
 *
 * **Nessun comando che modifica.** Non ci sono pulsanti per cancellare o
 * correggere righe, e l'API non espone gli endpoint per farlo: un registro
 * emendabile dal pannello non vale come prova.
 */
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { ApiError, admin, logs } from '../lib/api.js';
import { FUSO_AUDIT, formatIstanteAudit, formatOraAudit, formatSize } from '../lib/format.js';
import { useDebounced } from '../hooks/useDebounced.js';
import type { AuditFilters, AuditRow } from '../lib/types.js';
import './LogsPage.css';

const PAGINA = 50;

/** Etichette italiane delle azioni. Le chiavi sono quelle di `AUDIT_ACTIONS`. */
const AZIONE_LABEL: Record<string, string> = {
  login: 'Accesso',
  login_failed: 'Accesso rifiutato',
  logout: 'Uscita',
  switch_client: 'Cambio organizzazione',
  user_provisioned: 'Utenza abilitata',
  list: 'Navigazione',
  search: 'Ricerca',
  download: 'Download',
  upload: 'Caricamento',
  upload_session: 'Caricamento a blocchi',
  delete: 'Eliminazione',
  mkdir: 'Nuova cartella',
  rename: 'Rinomina',
  admin_change: 'Modifica amministrativa',
  audit_export: 'Esportazione log',
};

/**
 * Un'azione che l'API conosce e la UI no si mostra col suo nome tecnico.
 * L'alternativa — una cella vuota — nasconderebbe righe vere dopo il primo
 * `action` aggiunto al server senza toccare questo file.
 */
const azioneLabel = (a: string) => AZIONE_LABEL[a] ?? a;

const ESITO_LABEL: Record<string, string> = {
  ok: 'riuscita',
  denied: 'negata',
  error: 'errore',
};

/**
 * Gruppi di azioni, per non mettere quindici voci piatte in una tendina.
 *
 * L'ordine dentro ogni gruppo è quello in cui le si cerca, non alfabetico:
 * chi apre i log per un contenzioso cerca prima `download` e `delete`.
 */
const GRUPPI: { id: string; label: string; azioni: string[] }[] = [
  {
    id: 'g-documenti',
    label: 'Operazioni sui documenti',
    azioni: ['download', 'upload', 'upload_session', 'delete', 'mkdir', 'rename'],
  },
  {
    id: 'g-accessi',
    label: 'Accessi e sessioni',
    azioni: ['login', 'login_failed', 'logout', 'switch_client', 'user_provisioned'],
  },
  {
    id: 'g-navigazione',
    label: 'Navigazione e ricerca',
    azioni: ['list', 'search'],
  },
  {
    id: 'g-admin',
    label: 'Amministrazione',
    azioni: ['admin_change', 'audit_export'],
  },
];

const FILTRI_VUOTI: AuditFilters = {
  azione: [],
  esito: [],
  utente: '',
  organizzazione: null,
  percorso: '',
  richiesta: '',
  da: '',
  a: '',
};

/**
 * `datetime-local` vuole `YYYY-MM-DDTHH:mm` in ora **locale**: `toISOString()`
 * darebbe UTC, e in Italia l'intervallo partirebbe due ore prima di quello che
 * l'utente vede scritto nel campo.
 */
function perDatetimeLocal(d: Date): string {
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
}

const PERIODI: { label: string; giorni: number | null }[] = [
  { label: 'Oggi', giorni: 0 },
  { label: '7 giorni', giorni: 7 },
  { label: '30 giorni', giorni: 30 },
  { label: 'Sempre', giorni: null },
];

/**
 * Quale periodo rapido risulta scelto: si tiene in uno stato suo invece di
 * dedurlo dai campi data.
 *
 * Dedurlo sarebbe piu' pulito in teoria, ma non funziona: «7 giorni» scrive in
 * `da` l'istante del clic al minuto, e per riconoscerlo un render dopo bisogna
 * ricalcolarlo da `new Date()` — che nel frattempo e' avanzato. Il confronto
 * fallirebbe a partire dal minuto successivo e la pillola si spegnerebbe da
 * sola.
 *
 * In cambio va spento a mano quando le date si toccano in altro modo: se uno
 * scrive una data a mano, «7 giorni» non e' piu' vero.
 */
type Periodo = string | null;

export function LogsPage() {
  const [filtri, setFiltri] = useState<AuditFilters>(FILTRI_VUOTI);
  const [periodoScelto, setPeriodoScelto] = useState<Periodo>(null);
  const [aperta, setAperta] = useState<number | null>(null);

  /** Azzera tutto, pillola del periodo compresa. */
  const azzera = () => {
    setFiltri(FILTRI_VUOTI);
    setPeriodoScelto(null);
  };

  /* I campi di testo si digitano: senza debounce partirebbe una query per
     lettera, e su una tabella grande sono scansioni. */
  const utente = useDebounced(filtri.utente.trim(), 350);
  const percorso = useDebounced(filtri.percorso.trim(), 350);

  const applicati = useMemo<AuditFilters>(
    () => ({ ...filtri, utente, percorso }),
    [filtri, utente, percorso],
  );

  const meta = useQuery({
    queryKey: ['audit-meta'],
    queryFn: () => logs.meta(),
    staleTime: Infinity,
  });

  /* Le organizzazioni servono solo a riempire la tendina: l'endpoint è quello
     del pannello, non ne serve uno nuovo. */
  const organizzazioni = useQuery({
    queryKey: ['admin-clients'],
    queryFn: () => admin.clients(),
    staleTime: 5 * 60_000,
  });

  const pagine = useInfiniteQuery({
    queryKey: ['audit', applicati],
    queryFn: ({ pageParam }) => logs.page(applicati, pageParam, PAGINA),
    initialPageParam: null as string | null,
    getNextPageParam: (ultima) => ultima.prossimoCursore,
    /* Il registro cresce mentre lo si guarda: rifare la prima pagina a ogni
       fuoco sposterebbe le righe sotto il cursore di chi legge. */
    refetchOnWindowFocus: false,
  });

  const righe = pagine.data?.pages.flatMap((p) => p.righe) ?? [];
  const prima = pagine.data?.pages[0];
  const attivi = contaFiltriAttivi(applicati);

  /** Valore della tendina: un id di gruppo, una singola azione, o niente. */
  const azioneSelezionata =
    applicati.azione.length === 0
      ? ''
      : (GRUPPI.find(
          (g) =>
            g.azioni.length === applicati.azione.length &&
            g.azioni.every((a) => applicati.azione.includes(a)),
        )?.id ?? (applicati.azione.length === 1 ? applicati.azione[0]! : ''));

  const cambiaAzione = (valore: string) => {
    const gruppo = GRUPPI.find((g) => g.id === valore);
    setFiltri((f) => ({ ...f, azione: gruppo ? gruppo.azioni : valore ? [valore] : [] }));
  };

  /**
   * Correlazione su una singola chiamata HTTP: **azzera gli altri filtri**.
   *
   * Sommarli sarebbe la cosa naturale da scrivere e la cosa sbagliata da fare.
   * Si arriva qui da una riga trovata filtrando per azione, e la domanda che ci
   * si sta ponendo e' «cos'altro e' successo in quella richiesta»: tenere il
   * filtro precedente risponde «solo altre righe dello stesso tipo», cioe'
   * proprio quello che si sapeva gia'.
   */
  const correlaRichiesta = (richiesta: string) => {
    setPeriodoScelto(null);
    setFiltri({ ...FILTRI_VUOTI, richiesta });
  };

  const periodo = (p: { label: string; giorni: number | null }) => {
    setPeriodoScelto(p.label);
    if (p.giorni === null) {
      setFiltri((f) => ({ ...f, da: '', a: '' }));
      return;
    }
    const da = new Date();
    if (p.giorni === 0) da.setHours(0, 0, 0, 0);
    else da.setDate(da.getDate() - p.giorni);
    setFiltri((f) => ({ ...f, da: perDatetimeLocal(da), a: '' }));
  };

  /** Data scritta a mano: nessun periodo rapido descrive piu' l'intervallo. */
  const cambiaData = (campo: 'da' | 'a', valore: string) => {
    setPeriodoScelto(null);
    setFiltri((f) => ({ ...f, [campo]: valore }));
  };

  return (
    <div className="logs">
      <div className="logs__head">
        <h1>Log</h1>
        <p className="logs__lead">
          Registro delle operazioni del portale. Su SharePoint ogni azione risulta fatta
          dall'identità applicativa: <strong>questo è l'unico posto</strong> dove è registrato chi
          ha fatto cosa, e per quale organizzazione. È di sola lettura, anche per gli
          amministratori.
        </p>
      </div>

      <div className="card logs__filtri">
        <div className="logs__riga">
          <label className="logs__campo">
            <span>Azione</span>
            <select
              className="input"
              value={azioneSelezionata}
              onChange={(e) => cambiaAzione(e.target.value)}
            >
              <option value="">Tutte le azioni</option>
              <optgroup label="Gruppi">
                {GRUPPI.map((g) => (
                  <option key={g.id} value={g.id}>
                    {g.label}
                  </option>
                ))}
              </optgroup>
              <optgroup label="Azione singola">
                {(meta.data?.azioni ?? []).map((a) => (
                  <option key={a} value={a}>
                    {azioneLabel(a)}
                  </option>
                ))}
              </optgroup>
            </select>
          </label>

          <label className="logs__campo">
            <span>Organizzazione</span>
            <select
              className="input"
              value={applicati.organizzazione ?? ''}
              onChange={(e) =>
                setFiltri((f) => ({
                  ...f,
                  organizzazione: e.target.value ? Number(e.target.value) : null,
                }))
              }
            >
              <option value="">Tutte</option>
              {(organizzazioni.data?.clients ?? []).map((c) => (
                <option key={c.id} value={c.id}>
                  {c.displayName}
                </option>
              ))}
            </select>
          </label>

          <label className="logs__campo">
            <span>Utente</span>
            <input
              className="input"
              type="search"
              value={filtri.utente}
              placeholder="username o nome…"
              onChange={(e) => setFiltri((f) => ({ ...f, utente: e.target.value }))}
            />
          </label>

          <label className="logs__campo">
            <span>Percorso</span>
            <input
              className="input"
              type="search"
              value={filtri.percorso}
              placeholder="parte del percorso o del nome file…"
              onChange={(e) => setFiltri((f) => ({ ...f, percorso: e.target.value }))}
            />
          </label>
        </div>

        <div className="logs__riga logs__riga--date">
          <label className="logs__campo logs__campo--data">
            <span>Dal</span>
            <input
              className="input"
              type="datetime-local"
              value={filtri.da}
              onChange={(e) => cambiaData('da', e.target.value)}
            />
          </label>

          <label className="logs__campo logs__campo--data">
            <span>Al</span>
            <input
              className="input"
              type="datetime-local"
              value={filtri.a}
              onChange={(e) => cambiaData('a', e.target.value)}
            />
          </label>

          <div className="logs__periodi" role="group" aria-label="Periodo">
            {PERIODI.map((p) => {
              const attivo = periodoScelto === p.label;
              return (
                <button
                  key={p.label}
                  type="button"
                  aria-pressed={attivo}
                  className={attivo ? 'is-active' : undefined}
                  onClick={() => periodo(p)}
                >
                  {p.label}
                </button>
              );
            })}
          </div>

          <div className="logs__esiti" role="group" aria-label="Esito">
            {(meta.data?.esiti ?? ['ok', 'denied', 'error']).map((e) => {
              const attivo = applicati.esito.includes(e);
              return (
                <button
                  key={e}
                  type="button"
                  aria-pressed={attivo}
                  className={`logs__esito logs__esito--${e}${attivo ? ' is-active' : ''}`}
                  onClick={() =>
                    setFiltri((f) => ({
                      ...f,
                      esito: attivo ? f.esito.filter((x) => x !== e) : [...f.esito, e],
                    }))
                  }
                >
                  {ESITO_LABEL[e] ?? e}
                </button>
              );
            })}
          </div>
        </div>

        {applicati.richiesta ? (
          <div className="logs__riga logs__riga--chip">
            {/* Arriva dal clic su un id di richiesta in tabella: mette insieme
                tutte le righe generate da una singola chiamata HTTP. */}
            <span className="logs__chip">
              richiesta <code>{applicati.richiesta}</code>
              <button
                type="button"
                aria-label="Togli il filtro sulla richiesta"
                onClick={() => setFiltri((f) => ({ ...f, richiesta: '' }))}
              >
                ×
              </button>
            </span>
          </div>
        ) : null}
      </div>

      <div className="logs__barra">
        <p className="logs__conteggio">
          {pagine.isPending ? (
            'Caricamento…'
          ) : prima?.totale === null || prima === undefined ? (
            `${righe.length} righe`
          ) : (
            <>
              <strong>
                {prima.totaleTroncato ? `più di ${prima.totale!.toLocaleString('it-IT')}` : prima.totale!.toLocaleString('it-IT')}
              </strong>{' '}
              {prima.totale === 1 && !prima.totaleTroncato ? 'riga' : 'righe'}
              {attivi > 0 ? ' con i filtri attivi' : ' in tutto il registro'}
              {righe.length < (prima.totale ?? 0) ? ` — ${righe.length} caricate` : ''}
            </>
          )}
        </p>

        <div className="logs__azioni">
          {attivi > 0 ? (
            <button
              type="button"
              className="btn btn--ghost btn--sm"
              onClick={azzera}
            >
              Azzera filtri
            </button>
          ) : null}

          {/*
            Un link e non una fetch: il download lo gestisce il browser, con
            `Content-Disposition` e senza tenere il CSV in memoria. `download`
            non serve — il nome del file lo decide il server — ma il target
            evita che un errore sostituisca la pagina dei log.
          */}
          <a
            className="btn btn--sm"
            href={logs.exportUrl(applicati)}
            title="Scarica in CSV le righe che corrispondono ai filtri attivi"
          >
            Esporta CSV
          </a>
        </div>
      </div>

      {pagine.isError ? (
        <div className="alert alert--error" role="alert">
          {pagine.error instanceof ApiError ? pagine.error.message : 'Log non disponibili.'}
        </div>
      ) : null}

      <div className="card logs__panel">
        {pagine.isPending ? (
          <div className="logs__state">Caricamento…</div>
        ) : righe.length === 0 ? (
          <div className="logs__state">
            Nessuna riga con questi filtri.
            {attivi > 0 ? ' Prova ad allargare il periodo o ad azzerare i filtri.' : ''}
          </div>
        ) : (
          <div className="logs__scroll">
            <table className="logs__table">
              <thead>
                <tr>
                  <th scope="col">Data e ora</th>
                  <th scope="col">Azione</th>
                  <th scope="col">Utente</th>
                  <th scope="col">Organizzazione</th>
                  <th scope="col">Percorso</th>
                  <th scope="col" className="logs__col-apri">
                    <span className="visually-hidden">Dettagli</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {righe.map((r) => (
                  <Riga
                    key={r.id}
                    riga={r}
                    aperta={aperta === r.id}
                    onApri={() => setAperta((id) => (id === r.id ? null : r.id))}
                    onFiltraRichiesta={correlaRichiesta}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {righe.length > 0 ? (
        <p className="logs__fuso">
          Gli orari sono in ora italiana ({FUSO_AUDIT}), ora legale compresa.
        </p>
      ) : null}

      {pagine.hasNextPage ? (
        <div className="logs__altre">
          <button
            type="button"
            className="btn btn--ghost"
            disabled={pagine.isFetchingNextPage}
            onClick={() => void pagine.fetchNextPage()}
          >
            {pagine.isFetchingNextPage ? 'Caricamento…' : `Carica altre ${PAGINA}`}
          </button>
        </div>
      ) : righe.length > 0 ? (
        <p className="logs__fine">Fine del registro per questi filtri.</p>
      ) : null}
    </div>
  );
}

function Riga({
  riga,
  aperta,
  onApri,
  onFiltraRichiesta,
}: {
  riga: AuditRow;
  aperta: boolean;
  onApri: () => void;
  onFiltraRichiesta: (richiesta: string) => void;
}) {
  const dettaglio =
    riga.dettaglio !== null && riga.dettaglio !== undefined
      ? JSON.stringify(riga.dettaglio, null, 2)
      : null;

  return (
    <>
      <tr className={`logs__row logs__row--${riga.esito}${aperta ? ' is-aperta' : ''}`}>
        <td className="logs__ts">
          {formatOraAudit(riga.ts)}
          <span className="logs__esito-tag">{ESITO_LABEL[riga.esito] ?? riga.esito}</span>
        </td>
        <td>{azioneLabel(riga.azione)}</td>
        <td>
          {riga.utente ? (
            <>
              <strong>{riga.utente.displayName ?? riga.utente.username}</strong>
              {riga.utente.displayName ? (
                <span className="logs__sub">{riga.utente.username}</span>
              ) : null}
            </>
          ) : (
            /* Utenza cancellata (ON DELETE SET NULL) oppure azione senza
               sessione: un login rifiutato non ha un utente da attribuire. */
            <span className="logs__muted">—</span>
          )}
        </td>
        <td>
          {riga.organizzazione ? (
            <>
              {riga.organizzazione.displayName}
              <span className="logs__sub">{riga.organizzazione.code}</span>
            </>
          ) : (
            <span className="logs__muted">—</span>
          )}
        </td>
        <td className="logs__percorso" title={riga.percorso ?? ''}>
          {riga.percorso ?? <span className="logs__muted">—</span>}
        </td>
        <td className="logs__col-apri">
          <button
            type="button"
            className="logs__apri"
            aria-expanded={aperta}
            aria-label={aperta ? 'Chiudi dettagli' : 'Mostra dettagli'}
            onClick={onApri}
          >
            {aperta ? '−' : '+'}
          </button>
        </td>
      </tr>

      {aperta ? (
        <tr className="logs__dettaglio-row">
          <td colSpan={6}>
            <dl className="logs__dettaglio">
              <div>
                <dt>Istante esatto</dt>
                {/*
                  Ora italiana al millisecondo, e sotto lo stesso istante in UTC.
                  Non e' una ripetizione: l'ora italiana e' quella con cui si
                  discute di un fatto, l'UTC e' il valore immagazzinato ed e'
                  quello che combacia con i log applicativi del server.
                */}
                <dd>
                  {formatIstanteAudit(riga.ts)}
                  <span className="logs__sub">
                    <code>{riga.ts}</code> UTC
                  </span>
                </dd>
              </div>
              <div>
                <dt>Azione registrata</dt>
                <dd>
                  <code>{riga.azione}</code> · esito <code>{riga.esito}</code>
                </dd>
              </div>
              {riga.bytes !== null ? (
                <div>
                  <dt>Dimensione</dt>
                  <dd>{formatSize(riga.bytes)}</dd>
                </div>
              ) : null}
              <div>
                <dt>Indirizzo IP</dt>
                <dd>{riga.ip ? <code>{riga.ip}</code> : <span className="logs__muted">—</span>}</dd>
              </div>
              <div>
                <dt>Richiesta</dt>
                <dd>
                  {riga.richiesta ? (
                    <button
                      type="button"
                      className="logs__link"
                      title="Mostra tutte le righe di questa richiesta"
                      onClick={() => onFiltraRichiesta(riga.richiesta!)}
                    >
                      <code>{riga.richiesta}</code>
                    </button>
                  ) : (
                    <span className="logs__muted">—</span>
                  )}
                </dd>
              </div>
              {riga.userAgent ? (
                <div className="logs__dettaglio--largo">
                  <dt>Browser</dt>
                  <dd className="logs__ua">{riga.userAgent}</dd>
                </div>
              ) : null}
              {dettaglio ? (
                <div className="logs__dettaglio--largo">
                  <dt>Dettaglio</dt>
                  <dd>
                    <pre className="logs__json">{dettaglio}</pre>
                  </dd>
                </div>
              ) : null}
            </dl>
          </td>
        </tr>
      ) : null}
    </>
  );
}

function contaFiltriAttivi(f: AuditFilters): number {
  return (
    (f.azione.length ? 1 : 0) +
    (f.esito.length ? 1 : 0) +
    (f.utente ? 1 : 0) +
    (f.organizzazione !== null ? 1 : 0) +
    (f.percorso ? 1 : 0) +
    (f.richiesta ? 1 : 0) +
    (f.da ? 1 : 0) +
    (f.a ? 1 : 0)
  );
}
